import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FooterContainer,
  FooterContent,
  FooterTop,
  FooterSection,
  FooterSectionTitle,
  FooterLinks,
  FooterLinkItem,
  ContactDetails,
  FooterBottom,
  Copyright,
} from '../styles/FooterStyles';
import { get } from '../utils/apiClient';
import { getCachedData, setCachedData, isCacheStale } from '../utils/cacheUtils';

const CACHE_KEY = 'home_data';
const MAX_AGE_MS = 1000 * 60 * 60;

const Footer = () => {
  const [footer, setFooter] = useState(null);
  const [header, setHeader] = useState(null);
  const [contactInfo, setContactInfo] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      const cached = await getCachedData(CACHE_KEY);

      if (cached && cached.data) {
        if (!isMounted) return;
        if (cached.data.footer) {
          setFooter(cached.data.footer);
        }
        if (cached.data.header) {
          setHeader(cached.data.header);
        }
      }

      try {
        if (cached && !isCacheStale(cached, MAX_AGE_MS)) {
          return;
        }

        const data = await get('/home');
        if (!isMounted) return;

        if (data.footer) {
          setFooter(data.footer);
        }
        if (data.header) {
          setHeader(data.header);
        }

        setCachedData(CACHE_KEY, data);
      } catch (err) {
        // Footer can render with minimal content if the request fails.
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchContact = async () => {
      try {
        const data = await get('/contact');
        if (!isMounted) return;
        if (data && data.contactInfo) {
          setContactInfo(data.contactInfo);
        }
      } catch (err) {
        // Footer can still render without contact page data.
      }
    };

    fetchContact();

    return () => {
      isMounted = false;
    };
  }, []);

  const orgName =
    (footer && footer.organizationName) ||
    (header && header.organizationName) ||
    '';

  const phone = (contactInfo && contactInfo.phone) || (footer && footer.phone);
  const email = (contactInfo && contactInfo.email) || (footer && footer.email);
  const address = (contactInfo && contactInfo.address) || (footer && footer.address);

  return (
    <FooterContainer>
      <FooterContent>
        <FooterTop>
          <FooterSection>
            {orgName && (
              <FooterSectionTitle>{orgName}</FooterSectionTitle>
            )}
          </FooterSection>

          <FooterSection>
            <FooterSectionTitle>Quick Links</FooterSectionTitle>
            <FooterLinks>
              <FooterLinkItem>
                <Link to="/">Home</Link>
              </FooterLinkItem>
              <FooterLinkItem>
                <Link to="/about">About</Link>
              </FooterLinkItem>
              <FooterLinkItem>
                <Link to="/buildingproject">Building Project</Link>
              </FooterLinkItem>
              <FooterLinkItem>
                <Link to="/churchactivities">Activities</Link>
              </FooterLinkItem>
              <FooterLinkItem>
                <Link to="/gallery">Gallery</Link>
              </FooterLinkItem>
              <FooterLinkItem>
                <Link to="/testimonies">Testimonies</Link>
              </FooterLinkItem>
              <FooterLinkItem>
                <Link to="/contact">Contact</Link>
              </FooterLinkItem>
              <FooterLinkItem>
                <Link to="/donate">Donate</Link>
              </FooterLinkItem>
              <FooterLinkItem>
                <Link to="/admin">Admin Login</Link>
              </FooterLinkItem>
            </FooterLinks>
          </FooterSection>

          <FooterSection>
            <FooterSectionTitle>Contact Info</FooterSectionTitle>
            <ContactDetails>
              {address && <p>{address}</p>}
              {phone && <p>{phone}</p>}
              {email && <p>{email}</p>}
            </ContactDetails>
          </FooterSection>
        </FooterTop>

        <FooterBottom>
          <Copyright>
            {footer && footer.copyrightText
              ? footer.copyrightText
              : orgName
              ? `© ${new Date().getFullYear()} ${orgName}`
              : `© ${new Date().getFullYear()}`}
          </Copyright>
        </FooterBottom>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer;
